import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import DeckGL from "@deck.gl/react";
import { ArcLayer, ScatterplotLayer, PolygonLayer } from "@deck.gl/layers";
import { Map as MapGL } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import NavBar from "../../components/common/NavBar";
import MapAttribution from "../../components/common/MapAttribution";
import { basemapStyle } from "../../config/theme";
import { useThemeColors } from "../../hooks/useThemeColors";
import { fetchDemoOdmAnchored } from "../../services/demoApi";
import { fitView, outlineRings, readShape } from "../../geo/areaShapes";
import type { AreaShape } from "../../geo/areaShapes";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type Direction = "origin" | "destination";
type TimeMode = "hourly" | "daily";

interface ArcRow {
  origin_cell: string; dest_cell: string; trips: number;
  o_lat: number; o_lon: number; d_lat: number; d_lon: number;
}
interface OdmResponse {
  direction: Direction;
  rows: ArcRow[];
  hourly: { hour: number; trips: number }[];
  daily: { date: string; trips: number }[];
}

const fmtDate = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-surface rounded-lg shadow p-4">
    <div className="text-xs text-ink-subtle">{label}</div>
    <div className="text-2xl font-semibold text-ink">{value}</div>
  </div>
);
const Toggle: React.FC<{ options: [string, string]; value: string; onChange: (v: any) => void; labels?: [string, string] }> = ({ options, value, onChange, labels }) => (
  <div className="join">
    {options.map((o, i) => (
      <button key={o} className={`btn btn-xs join-item ${value === o ? "btn-primary" : "btn-ghost"}`} onClick={() => onChange(o)}>
        {labels ? labels[i] : o}
      </button>
    ))}
  </div>
);

const OdmDashboard: React.FC = () => {
  const colors = useThemeColors();
  const location = useLocation();
  const state = (location.state as any) || {};
  const selectedGeometries: any[] = useMemo(() => state.selectedGeometries || [], [state.selectedGeometries]);
  const selectedIds: string[] = useMemo(
    () => (state.selectedIds && state.selectedIds.length ? state.selectedIds : selectedGeometries.map((g) => String(g.geo_id))),
    [state.selectedIds, selectedGeometries]
  );
  const singleAreaMode = !!state.singleAreaMode; // arrived via the "single area" tab

  const dirLabels: [string, string] = singleAreaMode
    ? ["Trips out (to)", "Trips in (from)"]
    : ["Selected = Origin", "Selected = Destination"];

  const [direction, setDirection] = useState<Direction>("origin");
  const [timeMode, setTimeMode] = useState<TimeMode>("hourly");
  const [data, setData] = useState<OdmResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch the anchored ODM for the current direction (arcs + hourly + daily).
  useEffect(() => {
    if (!selectedIds.length) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDemoOdmAnchored<OdmResponse & { error?: string; detail?: string }>({ origin_ids: selectedIds.join(","), direction, limit: 400 })
      .then((d) => {
        if (cancelled) return;
        if (d.error) throw new Error(d.detail || d.error);
        setData(d);
      })
      .catch((e) => !cancelled && setError(String(e.message || e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [selectedIds, direction]);

  const anchorPolys = useMemo(
    () =>
      selectedGeometries
        .map((g) => {
          const shape = readShape(g.geometry);
          return { geo_id: String(g.geo_id), ring: shape ? outlineRings(shape)[0] ?? [] : [] };
        })
        .filter((p) => p.ring.length),
    [selectedGeometries]
  );
  const arcs = useMemo<ArcRow[]>(() => data?.rows ?? [], [data]);
  const maxTrips = useMemo(() => Math.max(1, ...arcs.map((a) => a.trips)), [arcs]);
  const totalTrips = useMemo(() => arcs.reduce((s, a) => s + a.trips, 0), [arcs]);
  const otherEnds = useMemo(() => {
    const s = new Set<string>();
    for (const a of arcs) s.add(direction === "origin" ? a.dest_cell : a.origin_cell);
    return s.size;
  }, [arcs, direction]);

  // Dots for the "other" end of each flow.
  const otherDots = useMemo(() => {
    const m = new Map<string, { lon: number; lat: number }>();
    for (const a of arcs) {
      if (direction === "origin") m.set(a.dest_cell, { lon: a.d_lon, lat: a.d_lat });
      else m.set(a.origin_cell, { lon: a.o_lon, lat: a.o_lat });
    }
    return Array.from(m.values());
  }, [arcs, direction]);

  const view = useMemo(() => {
    if (!arcs.length && !anchorPolys.length) return { longitude: 26.72, latitude: 58.37, zoom: 11, pitch: 35, bearing: 0 };
    // The flows' end points and the anchor areas, framed together.
    const shown: AreaShape[] = [
      ...arcs.flatMap((a): AreaShape[] => [
        { type: "Point", coordinates: [a.o_lon, a.o_lat] },
        { type: "Point", coordinates: [a.d_lon, a.d_lat] },
      ]),
      ...anchorPolys.map((p): AreaShape => ({ type: "LineString", coordinates: p.ring as [number, number][] })),
    ];
    return { ...fitView(shown, null), pitch: 35, bearing: 0 };
  }, [arcs, anchorPolys]);

  // origin mode: anchor is the source (origin colour out to destination-coloured dests).
  // destination mode: anchor is the target (origin-coloured origins in to the destination-coloured anchor).
  // Origin and destination use the same series pairing as the O-D map (blue, orange-red).
  const layers = useMemo(() => {
    const originRgb = colors.rgb("series-2");
    const destRgb = colors.rgb("series-3");
    const anchorRgb = direction === "origin" ? originRgb : destRgb;
    return [
    new PolygonLayer({
      id: "anchor",
      data: anchorPolys,
      getPolygon: (d: any) => d.ring,
      getFillColor: [...anchorRgb, 55],
      getLineColor: [...anchorRgb, 255],
      getLineWidth: 3,
      lineWidthMinPixels: 2,
      stroked: true,
      filled: true,
    }),
    new ScatterplotLayer({
      id: "other",
      data: otherDots,
      getPosition: (d: any) => [d.lon, d.lat],
      getRadius: 180,
      radiusMinPixels: 2,
      getFillColor: [...colors.rgb("series-10"), 150],
    }),
    new ArcLayer({
      id: "flows",
      data: arcs,
      getSourcePosition: (d: ArcRow) => [d.o_lon, d.o_lat],
      getTargetPosition: (d: ArcRow) => [d.d_lon, d.d_lat],
      getSourceColor: originRgb,
      getTargetColor: destRgb,
      getWidth: (d: ArcRow) => 1 + 9 * Math.sqrt(d.trips / maxTrips),
      getHeight: 0.4,
      greatCircle: false,
      opacity: 0.5,
      pickable: true,
    }),
    ];
  }, [anchorPolys, otherDots, arcs, direction, maxTrips, colors]);

  const chart = useMemo(() => {
    if (timeMode === "hourly") {
      const h = data?.hourly ?? [];
      return { labels: h.map((x) => `${x.hour}:00`), data: h.map((x) => x.trips) };
    }
    const d = data?.daily ?? [];
    return { labels: d.map((x) => fmtDate(x.date)), data: d.map((x) => x.trips) };
  }, [data, timeMode]);

  if (!selectedIds.length) {
    return (
      <div className="min-h-screen">
        <NavBar title="ODM Dashboard" showBackButton={true} />
        <div className="p-10 text-center text-ink-subtle">No area selected — run an ODM job, then click <span className="font-medium">View Dashboard</span>.</div>
      </div>
    );
  }

  const dirWord = direction === "origin" ? "go to" : "come from";
  return (
    <div className="min-h-screen bg-paper-200">
      <NavBar title="ODM Dashboard — Estonia (observed telco)" showBackButton={true} />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Selected cells" value={selectedIds.length.toLocaleString()} />
          <Stat label={direction === "origin" ? "Trips out / day" : "Trips in / day"} value={Math.round(totalTrips).toLocaleString()} />
          <Stat label={direction === "origin" ? "Destinations" : "Origins"} value={otherEnds.toLocaleString()} />
          <Stat label="Direction" value={direction === "origin" ? "Outflow" : "Inflow"} />
        </div>

        <div className="bg-surface rounded-lg shadow p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-base font-semibold text-ink">Flows — where trips {dirWord} the selected cells</h2>
              <p className="text-xs text-ink-subtle mb-3">Observed Tartu 1km trips/day. The selected cells are outlined; arc width = trip volume.</p>
            </div>
            <Toggle options={["origin", "destination"]} labels={dirLabels} value={direction} onChange={setDirection} />
          </div>
          <div className="relative w-full overflow-hidden rounded" style={{ height: 440 }}>
            {loading && <div className="absolute inset-0 z-10 flex items-center justify-center text-sm text-ink-subtle bg-surface/60">Loading flows…</div>}
            {error && <div className="absolute inset-0 z-10 flex items-center justify-center text-sm text-bad-ink bg-surface/80">Couldn’t reach demo API — {error}</div>}
            <DeckGL
              initialViewState={view}
              controller={true}
              layers={layers}
              style={{ position: "absolute", width: "100%", height: "100%" }}
              getTooltip={({ object }: any) => (object && object.origin_cell ? `${object.origin_cell} → ${object.dest_cell}\n${Math.round(object.trips).toLocaleString()} trips/day` : null)}
            >
              <MapGL mapStyle={basemapStyle(colors.dark)} attributionControl={false} />
            </DeckGL>
            <MapAttribution />
          </div>
        </div>

        <div className="bg-surface rounded-lg shadow p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-ink">{direction === "origin" ? "Trips out" : "Trips in"} — {timeMode === "hourly" ? "by hour of day" : "by day"}</h2>
              <p className="text-xs text-ink-subtle mb-3">Observed trips {dirWord} the selected cells (Tartu, 10–13 Nov 2025), derived from stay-to-stay moves.</p>
            </div>
            <Toggle options={["hourly", "daily"]} labels={["Hourly", "Daily"]} value={timeMode} onChange={setTimeMode} />
          </div>
          <div style={{ height: 300 }}>
            <Bar
              data={{ labels: chart.labels, datasets: [{ label: "Trips", data: chart.data, backgroundColor: colors.css("brand-500") }] }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                color: colors.chart.tick,
                plugins: { legend: { display: false } },
                scales: { x: { ticks: { color: colors.chart.tick }, grid: { color: colors.chart.grid } }, y: { ticks: { color: colors.chart.tick }, grid: { color: colors.chart.grid } } },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OdmDashboard;
